import React, { useEffect, useState, useMemo } from "react";
import { Wrench, Plus, Search, Edit2, Save, X, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { ImageStager } from "@/components/ImageStager";
import { authFetch } from "@/lib/auth";
import { uploadAttachment } from "@/lib/attachments";
import { toast } from "sonner";
import type { Tool } from "@shared/types";
export function ToolsPage() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Add form
  const [fName, setFName] = useState("");
  const [fType, setFType] = useState("");
  const [fCondition, setFCondition] = useState("");
  const [fLocation, setFLocation] = useState("");
  const [fNotes, setFNotes] = useState("");
  // Staged image attached after the tool is created.
  const [imageFile, setImageFile] = useState<File | null>(null);
  // Edit state
  const [editId, setEditId] = useState<string | null>(null);
  const [eName, setEName] = useState("");
  const [eType, setEType] = useState("");
  const [eCondition, setECondition] = useState("");
  const [eLocation, setELocation] = useState("");
  const [eNotes, setENotes] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const fetchTools = async () => {
    try {
      const data = await authFetch<{ items: Tool[] }>("/api/tools");
      setTools(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      console.error("[Tools]", msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTools();
  }, []);
  const filtered = useMemo(() => {
    return tools.filter((t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.type.toLowerCase().includes(search.toLowerCase())
    );
  }, [tools, search]);
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fName.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const created = await authFetch<{ id: string }>("/api/tools", {
        method: "POST",
        body: JSON.stringify({
          name: fName.trim(),
          type: fType.trim(),
          condition: fCondition.trim(),
          location: fLocation.trim(),
          notes: fNotes.trim(),
        }),
      });

      if (imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: "tool",
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? "unknown error"}`,
          );
        }
      }

      toast.success("Tool added");
      setFName(""); setFType(""); setFCondition(""); setFLocation(""); setFNotes(""); setImageFile(null);
      setDialogOpen(false);
      setLoading(true);
      fetchTools();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };
  const startEdit = (t: Tool) => {
    setEditId(t.id);
    setEName(t.name);
    setEType(t.type);
    setECondition(t.condition);
    setELocation(t.location);
    setENotes(t.notes);
  };
  const handleEditSave = async () => {
    if (!editId) return;
    setEditSaving(true);
    try {
      await authFetch(`/api/tools/${editId}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: eName.trim(),
          type: eType.trim(),
          condition: eCondition.trim(),
          location: eLocation.trim(),
          notes: eNotes.trim(),
        }),
      });
      toast.success("Tool updated");
      setEditId(null);
      setLoading(true);
      fetchTools();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };
  const handleArchive = async (id: string) => {
    try {
      await authFetch(`/api/tools/${id}`, { method: "DELETE" });
      toast.success("Tool archived");
      setTools((prev) => prev.filter((t) => t.id !== id));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to archive");
    }
  };
  return (
    <>
      <PageHeader title="Tools" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Tools" }]}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" /> Add Tool
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add Tool</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-2">
              <div className="space-y-2"><Label>Name *</Label><Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Chainsaw" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Type</Label><Input value={fType} onChange={(e) => setFType(e.target.value)} placeholder="Power Tool" /></div>
                <div className="space-y-2"><Label>Condition</Label><Input value={fCondition} onChange={(e) => setFCondition(e.target.value)} placeholder="Good" /></div>
              </div>
              <div className="space-y-2"><Label>Location</Label><Input value={fLocation} onChange={(e) => setFLocation(e.target.value)} placeholder="Barn Workshop" /></div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={fNotes} onChange={(e) => setFNotes(e.target.value)} rows={2} /></div>
              <div className="space-y-2">
                <Label>Image</Label>
                <ImageStager
                  file={imageFile}
                  onChange={setImageFile}
                  disabled={submitting}
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                {submitting ? "Adding..." : "Add Tool"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>
      {error && <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search tools..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>
      {loading ? (
        <CardGridSkeleton count={6} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title="No tools found"
          description={search ? "Try adjusting your search." : "Add your first tool to get started."}
          actionLabel={!search ? "Add Tool" : undefined}
          onAction={!search ? () => setDialogOpen(true) : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <Card key={t.id} className="p-5 hover:shadow-md transition-shadow duration-200">
              {editId === t.id ? (
                <div className="space-y-3">
                  <Input value={eName} onChange={(e) => setEName(e.target.value)} placeholder="Name" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={eType} onChange={(e) => setEType(e.target.value)} placeholder="Type" />
                    <Input value={eCondition} onChange={(e) => setECondition(e.target.value)} placeholder="Condition" />
                  </div>
                  <Input value={eLocation} onChange={(e) => setELocation(e.target.value)} placeholder="Location" />
                  <Textarea value={eNotes} onChange={(e) => setENotes(e.target.value)} rows={2} placeholder="Notes" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleEditSave} disabled={editSaving} className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90 flex-1">
                      <Save className="h-3 w-3 mr-1" /> {editSaving ? "Saving..." : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-5 w-5 text-ranch-brown" />
                      <h3 className="font-semibold text-foreground">{t.name}</h3>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-0.5 mb-2">
                    {t.type && <p>Type: <span className="font-medium text-foreground">{t.type}</span></p>}
                    {t.condition && <p>Condition: <span className="font-medium text-foreground">{t.condition}</span></p>}
                    {t.location && <p>Location: <span className="font-medium text-foreground">{t.location}</span></p>}
                  </div>
                  {t.notes && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{t.notes}</p>}
                  <div className="flex gap-2 pt-1 border-t">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(t)} className="text-muted-foreground hover:text-foreground">
                      <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Archive
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Archive {t.name}?</AlertDialogTitle><AlertDialogDescription>This will hide the tool from active lists.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleArchive(t.id)} className="bg-destructive text-destructive-foreground">Archive</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
